import type IBoundedHttpFetch from "./bounded-http-fetch.interface.ts";
import type IPublicAddressHttpFetch from "./public-address-http-fetch.interface.ts";

export type BoundedHttpFailure =
    | "content_type_rejected"
    | "http_status"
    | "invalid_url"
    | "network_failure"
    | "private_address"
    | "redirect_rejected"
    | "response_too_large"
    | "timeout";

export class BoundedHttpError extends Error {
    readonly failure: BoundedHttpFailure;
    readonly retryable: boolean;
    readonly status: number | undefined;

    constructor(
        failure: BoundedHttpFailure,
        message: string,
        options: { cause?: unknown; retryable?: boolean; status?: number } = {},
    ) {
        super(message, { cause: options.cause });
        this.name = "BoundedHttpError";
        this.failure = failure;
        this.retryable = options.retryable ?? false;
        this.status = options.status;
    }
}

export type BoundedHttpPolicy = {
    allowPrivateAddress?: boolean;
    allowedContentTypes?: readonly string[];
    allowedHosts?: readonly string[];
    acceptedStatuses?: readonly number[];
    allowedProtocols: readonly string[];
    maxRedirects: number;
    maxResponseBytes: number;
    requirePublicAddress?: boolean;
    timeoutMs: number;
};

export type BoundedHttpDependencies = {
    fetch?: IBoundedHttpFetch;
    publicAddressFetch?: IPublicAddressHttpFetch;
};

export type BoundedHttpStreamPolicy = BoundedHttpPolicy & {
    redirectMode?: "error" | "manual";
    timeoutScope?: "response" | "headers";
};

const redirectStatuses = new Set([301, 302, 303, 307, 308]);
type BoundedRequestFetch = (input: URL, init: RequestInit) => Promise<Response>;

function parseIpv4(value: string): [number, number, number, number] | null {
    const parts = value.split(".");
    if (parts.length === 0 || parts.length > 4) return null;
    const numbers: number[] = [];
    for (const part of parts) {
        if (part === "") return null;
        let number: number;
        if (/^0x[0-9a-f]+$/iu.test(part)) number = Number.parseInt(part, 16);
        else if (/^0[0-7]+$/u.test(part)) number = Number.parseInt(part, 8);
        else if (/^[0-9]+$/u.test(part)) number = Number.parseInt(part, 10);
        else return null;
        if (!Number.isFinite(number)) return null;
        numbers.push(number);
    }
    let packed: number;
    if (numbers.length === 4) {
        if (numbers.some((number) => number > 0xff)) return null;
        packed =
            ((numbers[0] << 24) |
                (numbers[1] << 16) |
                (numbers[2] << 8) |
                numbers[3]) >>>
            0;
    } else if (numbers.length === 3) {
        if (numbers[0] > 0xff || numbers[1] > 0xff || numbers[2] > 0xffff) {
            return null;
        }
        packed = ((numbers[0] << 24) | (numbers[1] << 16) | numbers[2]) >>> 0;
    } else if (numbers.length === 2) {
        if (numbers[0] > 0xff || numbers[1] > 0xffffff) return null;
        packed = ((numbers[0] << 24) | numbers[1]) >>> 0;
    } else {
        if (numbers[0] > 0xffffffff) return null;
        packed = numbers[0] >>> 0;
    }
    return [
        (packed >>> 24) & 0xff,
        (packed >>> 16) & 0xff,
        (packed >>> 8) & 0xff,
        packed & 0xff,
    ];
}

function isPrivateIpv4([a, b]: [number, number, number, number]): boolean {
    return (
        a === 0 ||
        a === 10 ||
        a === 127 ||
        (a === 169 && b === 254) ||
        (a === 172 && b >= 16 && b <= 31) ||
        (a === 192 && b === 168) ||
        (a === 100 && b >= 64 && b <= 127)
    );
}

export function isPrivateNetworkAddress(value: string): boolean {
    const normalized = value.toLowerCase().replace(/^\[|\]$/gu, "");
    const ipv4 = parseIpv4(normalized);
    if (ipv4) return isPrivateIpv4(ipv4);
    if (normalized === "::" || normalized === "::1") return true;
    if (/^f[cd][0-9a-f]{2}:/u.test(normalized)) return true;
    if (/^fe[89ab][0-9a-f]:/u.test(normalized)) return true;
    const dottedTail = normalized.slice(normalized.lastIndexOf(":") + 1);
    const mappedIpv4 = dottedTail.includes(".") ? parseIpv4(dottedTail) : null;
    if (mappedIpv4) return isPrivateIpv4(mappedIpv4);
    const mapped = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/u.exec(normalized);
    if (!mapped) return false;
    const high = Number.parseInt(mapped[1], 16);
    const low = Number.parseInt(mapped[2], 16);
    return isPrivateIpv4([
        (high >>> 8) & 0xff,
        high & 0xff,
        (low >>> 8) & 0xff,
        low & 0xff,
    ]);
}

async function validateUrl(
    value: string | URL,
    policy: BoundedHttpPolicy,
): Promise<URL> {
    let url: URL;
    try {
        url = new URL(value);
    } catch (cause) {
        throw new BoundedHttpError("invalid_url", "Request URL is invalid.", {
            cause,
        });
    }
    if (url.username || url.password) {
        throw new BoundedHttpError(
            "invalid_url",
            "Request URL credentials are not allowed.",
        );
    }
    if (!policy.allowedProtocols.includes(url.protocol)) {
        throw new BoundedHttpError(
            "invalid_url",
            "Request URL protocol is not allowed.",
        );
    }
    if (policy.allowedHosts && !policy.allowedHosts.includes(url.host)) {
        throw new BoundedHttpError(
            "invalid_url",
            "Request URL host is not allowed.",
        );
    }
    const hostname = url.hostname.toLowerCase().replace(/^\[|\]$/gu, "");
    if (
        !policy.allowPrivateAddress &&
        (hostname === "" ||
            hostname === "localhost" ||
            hostname.endsWith(".local") ||
            hostname.endsWith(".internal") ||
            isPrivateNetworkAddress(hostname))
    ) {
        throw new BoundedHttpError(
            "private_address",
            "Request URL resolves to a private address.",
        );
    }
    return url;
}

async function readBoundedBody(response: Response, maxBytes: number) {
    const declaredLength = Number(response.headers.get("content-length"));
    if (
        response.body &&
        Number.isFinite(declaredLength) &&
        declaredLength > maxBytes
    ) {
        await response.body.cancel().catch(() => {});
        throw new BoundedHttpError(
            "response_too_large",
            "Response exceeds the configured byte limit.",
        );
    }
    if (!response.body) {
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (bytes.byteLength > maxBytes) {
            throw new BoundedHttpError(
                "response_too_large",
                "Response exceeds the configured byte limit.",
            );
        }
        return bytes;
    }
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let byteLength = 0;
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;
        byteLength += value.byteLength;
        if (byteLength > maxBytes) {
            await reader.cancel().catch(() => {});
            throw new BoundedHttpError(
                "response_too_large",
                "Response exceeds the configured byte limit.",
            );
        }
        chunks.push(value);
    }
    const bytes = new Uint8Array(byteLength);
    let offset = 0;
    for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return bytes;
}

function validatePolicy(policy: BoundedHttpPolicy) {
    if (
        !Number.isInteger(policy.timeoutMs) ||
        policy.timeoutMs <= 0 ||
        !Number.isInteger(policy.maxResponseBytes) ||
        policy.maxResponseBytes <= 0 ||
        !Number.isInteger(policy.maxRedirects) ||
        policy.maxRedirects < 0
    ) {
        throw new BoundedHttpError(
            "invalid_url",
            "Bounded HTTP policy values are invalid.",
        );
    }
}

function selectFetch(
    policy: BoundedHttpPolicy,
    dependencies: BoundedHttpDependencies,
): BoundedRequestFetch {
    if (!policy.requirePublicAddress) {
        return dependencies.fetch ?? globalThis.fetch;
    }
    if (!dependencies.publicAddressFetch) {
        throw new BoundedHttpError(
            "private_address",
            "Public-address validation requires a protected transport.",
        );
    }
    return dependencies.publicAddressFetch.fetch.bind(
        dependencies.publicAddressFetch,
    );
}

async function requestResponse(
    fetchImpl: BoundedRequestFetch,
    current: URL,
    requestSignal: AbortSignal,
    timeoutSignal: AbortSignal,
    request: Pick<RequestInit, "body" | "headers" | "method">,
) {
    try {
        return await fetchImpl(current, {
            ...request,
            redirect: "manual",
            signal: requestSignal,
        });
    } catch (cause) {
        const timedOut = timeoutSignal.aborted;
        throw new BoundedHttpError(
            timedOut ? "timeout" : "network_failure",
            timedOut ? "Request timed out." : "Request failed.",
            { cause, retryable: true },
        );
    }
}

async function redirectUrl(
    response: Response,
    current: URL,
    policy: BoundedHttpPolicy,
    redirectCount: number,
) {
    if (!redirectStatuses.has(response.status)) return null;
    await response.body?.cancel().catch(() => {});
    const location = response.headers.get("location");
    if (!location || redirectCount >= policy.maxRedirects) {
        throw new BoundedHttpError(
            "redirect_rejected",
            "Response redirect is not allowed.",
            { status: response.status },
        );
    }
    try {
        return await validateUrl(new URL(location, current), policy);
    } catch (cause) {
        if (cause instanceof BoundedHttpError) throw cause;
        throw new BoundedHttpError("invalid_url", "Redirect URL is invalid.", {
            cause,
        });
    }
}

async function validateResponse(response: Response, policy: BoundedHttpPolicy) {
    if (!response.ok && !policy.acceptedStatuses?.includes(response.status)) {
        await response.body?.cancel().catch(() => {});
        throw new BoundedHttpError(
            "http_status",
            "Request returned an unsuccessful status.",
            {
                retryable: response.status === 429 || response.status >= 500,
                status: response.status,
            },
        );
    }
    const contentType =
        response.headers
            .get("content-type")
            ?.split(";", 1)[0]
            ?.trim()
            .toLowerCase() ?? null;
    if (
        policy.allowedContentTypes &&
        (!contentType || !policy.allowedContentTypes.includes(contentType))
    ) {
        await response.body?.cancel().catch(() => {});
        throw new BoundedHttpError(
            "content_type_rejected",
            "Response content type is not allowed.",
        );
    }
    return contentType;
}

async function readResponseBytes(
    response: Response,
    policy: BoundedHttpPolicy,
    timeoutSignal: AbortSignal,
) {
    try {
        return await readBoundedBody(response, policy.maxResponseBytes);
    } catch (cause) {
        if (cause instanceof BoundedHttpError) throw cause;
        const timedOut = timeoutSignal.aborted;
        throw new BoundedHttpError(
            timedOut ? "timeout" : "network_failure",
            timedOut ? "Request timed out." : "Response body failed.",
            { cause, retryable: true },
        );
    }
}

export async function fetchBoundedBytes(
    value: string | URL,
    policy: BoundedHttpPolicy,
    dependencies: BoundedHttpDependencies = {},
    signal?: AbortSignal,
): Promise<{
    bytes: Uint8Array;
    contentType: string | null;
    headers: Headers;
    status: number;
    url: string;
}> {
    return await fetchBoundedRequestBytes(
        value,
        policy,
        {},
        dependencies,
        signal,
    );
}

export async function fetchBoundedRequestBytes(
    value: string | URL,
    policy: BoundedHttpPolicy,
    request: Pick<RequestInit, "body" | "headers" | "method">,
    dependencies: BoundedHttpDependencies = {},
    signal?: AbortSignal,
): Promise<{
    bytes: Uint8Array;
    contentType: string | null;
    headers: Headers;
    status: number;
    url: string;
}> {
    if (
        request.method &&
        !["GET", "HEAD"].includes(request.method.toUpperCase()) &&
        policy.maxRedirects !== 0
    ) {
        throw new BoundedHttpError(
            "redirect_rejected",
            "Non-read requests must reject redirects.",
        );
    }
    validatePolicy(policy);
    const fetchImpl = selectFetch(policy, dependencies);
    const timeoutSignal = AbortSignal.timeout(policy.timeoutMs);
    const requestSignal = signal
        ? AbortSignal.any([signal, timeoutSignal])
        : timeoutSignal;
    let current = await validateUrl(value, policy);
    for (let redirectCount = 0; ; redirectCount += 1) {
        const response = await requestResponse(
            fetchImpl,
            current,
            requestSignal,
            timeoutSignal,
            request,
        );
        const redirected = await redirectUrl(
            response,
            current,
            policy,
            redirectCount,
        );
        if (redirected) {
            current = redirected;
            continue;
        }
        const contentType = await validateResponse(response, policy);
        const bytes = await readResponseBytes(response, policy, timeoutSignal);
        return {
            bytes,
            contentType,
            headers: response.headers,
            status: response.status,
            url: current.href,
        };
    }
}

export async function requestBoundedBytes(
    value: string | URL,
    init: RequestInit,
    policy: BoundedHttpPolicy,
    dependencies: BoundedHttpDependencies = {},
    signal?: AbortSignal,
) {
    return await fetchBoundedRequestBytes(
        value,
        policy,
        {
            body: init.body,
            headers: init.headers,
            method: init.method,
        },
        dependencies,
        signal,
    );
}

export async function fetchBoundedRequestStream(
    value: string | URL,
    policy: BoundedHttpStreamPolicy,
    request: Pick<RequestInit, "body" | "headers" | "method">,
    dependencies: BoundedHttpDependencies = {},
    signal?: AbortSignal,
): Promise<Response> {
    validatePolicy(policy);
    if (policy.maxRedirects !== 0)
        throw new BoundedHttpError(
            "redirect_rejected",
            "Streaming requests must reject redirects.",
        );
    const url = await validateUrl(value, policy);
    const headerDeadline =
        policy.timeoutScope === "headers" ? new AbortController() : undefined;
    const timeout =
        headerDeadline?.signal ?? AbortSignal.timeout(policy.timeoutMs);
    const timer = headerDeadline
        ? setTimeout(
              () =>
                  headerDeadline.abort(
                      new DOMException("Request timed out.", "TimeoutError"),
                  ),
              policy.timeoutMs,
          )
        : undefined;
    let response: Response;
    try {
        response = await requestResponse(
            selectFetch(policy, dependencies),
            url,
            signal ? AbortSignal.any([signal, timeout]) : timeout,
            timeout,
            request,
        );
    } finally {
        if (timer !== undefined) clearTimeout(timer);
    }
    if (policy.redirectMode !== "manual")
        await redirectUrl(response, url, policy, 0);
    await validateResponse(response, policy);
    if (
        response.body &&
        Number(response.headers.get("content-length")) > policy.maxResponseBytes
    ) {
        await response.body.cancel();
        throw new BoundedHttpError(
            "response_too_large",
            "Response exceeds the configured byte limit.",
        );
    }
    let received = 0;
    const body =
        response.body?.pipeThrough(
            new TransformStream<Uint8Array, Uint8Array>({
                transform(chunk, controller) {
                    received += chunk.byteLength;
                    if (received > policy.maxResponseBytes)
                        throw new BoundedHttpError(
                            "response_too_large",
                            "Response exceeds the configured byte limit.",
                        );
                    controller.enqueue(chunk);
                },
            }),
        ) ?? null;
    return new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
    });
}
